<template>
  <div class="p-4">
    <div class="flex align-items-center justify-content-between mb-4">
      <h2 class="m-0">Grupos de invitados</h2>
    </div>

    <div class="card mb-4">
      <div class="flex align-items-center justify-content-end mb-3 flex-wrap gap-2">
        <Button
          icon="pi pi-download"
          label="Plantilla"
          size="small"
          severity="secondary"
          outlined
          @click="downloadGuestGroupsTemplate"
        />
        <FileUpload
          mode="basic"
          chooseLabel="Importar CSV"
          chooseIcon="pi pi-upload"
          accept=".csv,text/csv"
          @select="handleImportGroupsFile"
        />
        <Button icon="pi pi-plus" label="Nuevo" size="small" @click="openCreateGroup" />
      </div>

      <div class="flex align-items-center flex-wrap gap-2 mb-3">
        <label class="text-500 text-sm">Filtrar por zona</label>
        <Select
          v-model="zonaFiltro"
          :options="zonaFiltroOptions"
          option-label="name"
          option-value="id"
          placeholder="Todas las zonas"
          showClear
          class="w-15rem"
        />
        <label class="text-500 text-sm">Buscar</label>
        <InputText v-model="textoFiltro" placeholder="Buscar por código o encargado" class="w-15rem" />
        <label class="text-500 text-sm">Día</label>
        <InputNumber v-model="diaFiltro" placeholder="Ej: 15" :min="1" :max="31" showButtons buttonLayout="horizontal" class="w-9rem" inputClass="w-4rem" />
      </div>

      <DataTable :value="guestGroupsFiltrados" :loading="loadingGroups" size="small" stripedRows>
        <Column field="codigo" header="Código" />
        <Column field="cantidad" header="Personas" style="width: 90px; text-align:center" />
        <Column field="autos" header="Autos" style="width: 80px; text-align:center" />
        <Column field="fechas" header="Fechas" sortable />
        <Column header="Zona" style="width: 160px">
          <template #body="{ data }">
            <Select
              :modelValue="data.zonaId"
              @update:modelValue="(val) => assignZona(data, val)"
              :options="zonas"
              option-label="name"
              option-value="id"
              placeholder="Sin zona"
              showClear
              class="w-full"
            />
          </template>
        </Column>
        <Column field="encargado" header="Encargado" />
        <Column header="Grupos de predicación">
          <template #body="{ data }">
            <span v-if="data.grupos?.length">{{ gruposNames(data.grupos) }}</span>
            <span v-else class="text-500">-</span>
          </template>
        </Column>
        <Column header="Territorios asignados">
          <template #body="{ data }">
            <span v-if="territoriosAsignados(data).length">{{ territoriosAsignados(data).join(', ') }}</span>
            <span v-else class="text-500">-</span>
          </template>
        </Column>
        <Column field="whatsapp" header="WhatsApp">
          <template #body="{ data }">
            <a v-if="data.whatsapp" :href="waLink(data.whatsapp)" target="_blank" rel="noopener">
              {{ data.whatsapp }}
            </a>
            <span v-else>-</span>
          </template>
        </Column>
        <Column header="En grupo WhatsApp" style="width: 100px; text-align:center">
          <template #body="{ data }">
            <Checkbox
              :modelValue="!!data.enGrupoWhatsapp"
              binary
              v-tooltip="'¿Ya está agregado al grupo de WhatsApp?'"
              @update:modelValue="(val) => toggleEnGrupoWhatsapp(data, val)"
            />
          </template>
        </Column>
        <Column header="" style="width: 100px">
          <template #body="{ data }">
            <div class="flex gap-1 justify-content-end">
              <Button
                icon="pi pi-whatsapp"
                size="small"
                severity="success"
                text
                :disabled="!data.whatsapp"
                v-tooltip="data.whatsapp ? 'Enviar mensaje' : 'Sin WhatsApp cargado'"
                @click="openMessageDialog(data)"
              />
              <Button
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                text
                v-tooltip="'Editar'"
                @click="openEditGroup(data)"
              />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Eliminar'"
                @click="confirmDeleteGroup(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Dialog: crear/editar grupo de invitados -->
    <Dialog
      v-model:visible="showGroupDialog"
      :header="editingGroup ? 'Editar grupo de invitados' : 'Nuevo grupo de invitados'"
      modal
      style="width: 420px"
    >
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Código</label>
          <InputText v-model="groupForm.codigo" placeholder="Ej: INV-01" autofocus />
        </div>
        <div class="flex flex-column gap-1">
          <label>Cantidad de personas</label>
          <InputNumber v-model="groupForm.cantidad" :min="0" showButtons />
        </div>
        <div class="flex flex-column gap-1">
          <label>Cantidad de autos</label>
          <InputNumber v-model="groupForm.autos" :min="0" showButtons />
        </div>
        <div class="flex flex-column gap-1">
          <label>Fechas</label>
          <InputText v-model="groupForm.fechas" placeholder="Ej: 15 y 16 de agosto" />
        </div>
        <div class="flex flex-column gap-1">
          <label>Zona</label>
          <Select
            v-model="groupForm.zonaId"
            :options="zonas"
            option-label="name"
            option-value="id"
            placeholder="Seleccione zona"
            showClear
          />
        </div>
        <div class="flex flex-column gap-1">
          <label>Grupos de predicación</label>
          <MultiSelect
            v-model="groupForm.grupos"
            :options="gruposPredicacion"
            option-label="name"
            option-value="id"
            placeholder="Seleccione grupos de predicación"
            display="chip"
          />
          <small class="text-500">La salida mostrará este grupo de invitados a través de los grupos de predicación seleccionados.</small>
        </div>
        <div class="flex flex-column gap-1">
          <label>Encargado</label>
          <InputText v-model="groupForm.encargado" placeholder="Nombre del encargado" />
        </div>
        <div class="flex flex-column gap-1">
          <label>WhatsApp del encargado</label>
          <InputText v-model="groupForm.whatsapp" placeholder="+54 9 11 5555 5555" />
        </div>
        <div class="flex align-items-center gap-2">
          <Checkbox v-model="groupForm.enGrupoWhatsapp" binary inputId="enGrupoWhatsapp" />
          <label for="enGrupoWhatsapp">Ya está en el grupo de WhatsApp</label>
        </div>
        <div v-if="errorGroup" class="text-red-500 text-sm">{{ errorGroup }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showGroupDialog = false" />
          <Button label="Guardar" :loading="savingGroup" @click="saveGuestGroup" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: resumen de importación CSV -->
    <Dialog v-model:visible="showImportSummary" header="Resultado de la importación" modal style="width: 480px">
      <div class="flex flex-column gap-3">
        <div class="flex gap-2 flex-wrap">
          <Tag severity="success" :value="`${importSummary.created.length} creados`" />
          <Tag severity="warn" :value="`${importSummary.skipped.length} omitidos`" />
          <Tag severity="danger" :value="`${importSummary.errors.length} con error`" />
        </div>
        <div v-if="importSummary.skipped.length">
          <strong>Omitidos (código duplicado)</strong>
          <ul class="m-0 pl-3">
            <li v-for="(s, idx) in importSummary.skipped" :key="idx">
              Línea {{ s.line }} ({{ s.codigo }}): {{ s.reason }}
            </li>
          </ul>
        </div>
        <div v-if="importSummary.errors.length">
          <strong>Errores</strong>
          <ul class="m-0 pl-3">
            <li v-for="(e, idx) in importSummary.errors" :key="idx">
              Línea {{ e.line }}: {{ e.reason }}
            </li>
          </ul>
        </div>
        <div v-if="importSummary.unresolvedGroups?.length" class="text-500 text-sm">
          Algunos "grupos de predicación" del CSV no coincidían con los configurados y se guardaron con el nombre tal cual: {{ importSummary.unresolvedGroups.map(g => g.nombre).join(', ') }}.
        </div>
        <div v-if="importSummary.unresolvedZonas?.length" class="text-500 text-sm">
          Algunas "zonas" del CSV no coincidían con las configuradas y se importaron sin zona asignada: {{ importSummary.unresolvedZonas.map(z => z.nombre).join(', ') }}.
        </div>
        <div class="flex justify-content-end">
          <Button label="Cerrar" @click="showImportSummary = false" />
        </div>
      </div>
    </Dialog>

    <!-- Dialog: enviar mensaje a un grupo de invitados -->
    <Dialog v-model:visible="showMessageDialog" header="Enviar mensaje por WhatsApp" modal style="width: 480px">
      <div class="flex flex-column gap-3 pt-2">
        <div v-if="messageGroup" class="text-500 text-sm">
          Para {{ messageGroup.encargado || messageGroup.codigo }} ({{ messageGroup.whatsapp }})
        </div>
        <div v-if="plantillas.length" class="flex flex-column gap-1">
          <label class="text-sm">Plantilla</label>
          <Select
            v-model="selectedPlantillaId"
            :options="plantillas"
            option-label="nombre"
            option-value="id"
            class="w-full"
          />
        </div>
        <div v-if="linkEstudiosUrl" class="flex flex-column gap-1">
          <label class="text-sm">Link al dashboard de estudios</label>
          <div class="flex align-items-center gap-1">
            <span
              class="text-xs text-400 font-mono overflow-hidden text-overflow-ellipsis white-space-nowrap flex-1"
            >{{ linkEstudiosUrl }}</span>
            <Button
              :icon="copiedEstudiosLink ? 'pi pi-check' : 'pi pi-copy'"
              size="small"
              text
              :severity="copiedEstudiosLink ? 'success' : 'secondary'"
              v-tooltip="'Copiar enlace'"
              @click="copyEstudiosLink"
            />
            <Button
              icon="pi pi-external-link"
              size="small"
              text
              severity="secondary"
              v-tooltip="'Abrir dashboard'"
              @click="openEstudiosLink"
            />
          </div>
        </div>
        <Textarea v-model="messageDraft" rows="8" autoResize />
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showMessageDialog = false" />
          <Button icon="pi pi-whatsapp" label="Enviar por WhatsApp" @click="sendMessageWhatsapp" />
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useGruposInvitados } from '@/composables/useGruposInvitados'
import { useEstudios } from '@/composables/useEstudios'
import { useCatalogos } from '@/composables/useCatalogos'
import { parseGuestGroupsCsv, buildGuestGroupsTemplateCsv } from '@/composables/useGuestGroupsCsv'
import {
  useMensajePlantilla,
  DEFAULT_MENSAJE_PLANTILLA,
  nuevaPlantilla,
  renderMensajePlantilla,
} from '@/composables/useMensajePlantilla'
import { useSalidaStore } from '@/store/useSalidaStore'
import { useConfirm } from 'primevue/useconfirm'
import { useAppToast } from '@/composables/useAppToast'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import MultiSelect from 'primevue/multiselect'
import Checkbox from 'primevue/checkbox'
import Tag from 'primevue/tag'
import Select from 'primevue/dropdown'
import ConfirmDialog from 'primevue/confirmdialog'
import FileUpload from 'primevue/fileupload'
import Textarea from 'primevue/textarea'

const confirm = useConfirm()
const toast = useAppToast()

const salidaStore = useSalidaStore()
const gruposInvitadosApi = useGruposInvitados()
const cat = useCatalogos()
const guestGroups = ref([])
const loadingGroups = ref(false)
const showGroupDialog = ref(false)
const editingGroup = ref(null)
const savingGroup = ref(false)
const errorGroup = ref(null)
const emptyGroupForm = () => ({ codigo: '', cantidad: 0, autos: 0, fechas: '', zonaId: null, encargado: '', whatsapp: '', grupos: [], enGrupoWhatsapp: false })
const groupForm = ref(emptyGroupForm())
const showImportSummary = ref(false)
const importSummary = ref({ created: [], skipped: [], errors: [], unresolvedGroups: [], unresolvedZonas: [] })

// Grupos de predicación (config); se cargan si la store aún no los tiene
const gruposPredicacion = ref([])
// Zonas (otras congregaciones); mismo catálogo que en Parámetros
const zonas = ref([])

const SIN_ZONA = '__sin_zona__'
const zonaFiltro = ref(null)
const textoFiltro = ref('')
const diaFiltro = ref(null)
const zonaFiltroOptions = computed(() => [{ id: SIN_ZONA, name: 'Sin zona' }, ...zonas.value])

// El campo "fechas" es texto libre, pero en la práctica se carga como un rango de
// días del mes (ej: "6 - 26"). Se toman los dos primeros números encontrados como
// día de inicio y fin para poder filtrar por un día puntual dentro de ese rango.
const parseFechasRango = (fechas) => {
  const nums = String(fechas || '').match(/\d+/g)
  if (!nums || nums.length < 2) return null
  const [a, b] = nums.map(Number)
  return { desde: Math.min(a, b), hasta: Math.max(a, b) }
}

const guestGroupsFiltrados = computed(() => {
  let result = guestGroups.value
  if (zonaFiltro.value === SIN_ZONA) result = result.filter((g) => !g.zonaId)
  else if (zonaFiltro.value) result = result.filter((g) => g.zonaId === zonaFiltro.value)
  const texto = textoFiltro.value.trim().toLowerCase()
  if (texto) {
    result = result.filter(
      (g) => (g.codigo || '').toLowerCase().includes(texto) || (g.encargado || '').toLowerCase().includes(texto)
    )
  }
  if (diaFiltro.value != null && diaFiltro.value !== '') {
    result = result.filter((g) => {
      const rango = parseFechasRango(g.fechas)
      return rango && diaFiltro.value >= rango.desde && diaFiltro.value <= rango.hasta
    })
  }
  return result
})

const gruposNames = (ids) =>
  (ids || [])
    .map((id) => gruposPredicacion.value.find((g) => g.id === id)?.name || id)
    .join(', ')

// Territorios que se le asignaron a un grupo invitado para predicar. La relación es
// la misma que usa FormSalidas.vue (gruposInvitadosDeSalida, en sentido inverso): una
// salida se vincula al grupo invitado cuando comparten algún grupo de predicación.
const territoriosAsignados = (grupo) => {
  if (!grupo.grupos?.length) return []
  const set = new Set()
  ;(salidaStore.list.data || [])
    .filter((s) => (s.grupos || []).some((gid) => grupo.grupos.includes(gid)))
    .forEach((s) => (s.territorios || []).forEach((t) => set.add(t)))
  return [...set].sort()
}

const loadZonas = async () => {
  const res = await cat.getZonas()
  zonas.value = res.zonas || []
}

const loadGruposPredicacion = async () => {
  const res = await cat.getGrupos()
  gruposPredicacion.value = res.grupos || []
}

const waLink = (w) => 'https://wa.me/' + String(w).replace(/\D/g, '')

// Plantillas de mensaje de WhatsApp para grupos de invitados (varias, con nombre)
const mensajeApi = useMensajePlantilla()
const estudiosApi = useEstudios()
const plantillas = ref([])

const showMessageDialog = ref(false)
const messageGroup = ref(null)
const messageDraft = ref('')
const selectedPlantillaId = ref(null)
const linkEstudiosUrl = ref('')
const copiedEstudiosLink = ref(false)

const zonaNombre = (zonaId) => zonas.value.find((z) => z.id === zonaId)?.name || ''

const mensajeVars = (grupo) => ({
  codigo: grupo.codigo || '',
  encargado: grupo.encargado || '',
  cantidad: String(grupo.cantidad ?? ''),
  autos: String(grupo.autos ?? ''),
  fechas: grupo.fechas || '',
  zona: zonaNombre(grupo.zonaId),
  grupos: grupo.grupos?.length ? gruposNames(grupo.grupos) : '',
  territorios: territoriosAsignados(grupo).join(', '),
  linkEstudios: linkEstudiosUrl.value,
})

const loadPlantillas = async () => {
  const result = await mensajeApi.getPlantillas()
  plantillas.value = result.length ? result : [nuevaPlantilla('Predeterminada', DEFAULT_MENSAJE_PLANTILLA)]
}

const updateMessageDraft = () => {
  if (!messageGroup.value) return
  const plantilla = plantillas.value.find((p) => p.id === selectedPlantillaId.value)
  messageDraft.value = renderMensajePlantilla(plantilla?.texto, mensajeVars(messageGroup.value))
}

watch(selectedPlantillaId, updateMessageDraft)

const openMessageDialog = async (grupo) => {
  messageGroup.value = grupo
  selectedPlantillaId.value = plantillas.value[0]?.id || null
  linkEstudiosUrl.value = ''
  copiedEstudiosLink.value = false
  updateMessageDraft()
  showMessageDialog.value = true
  if (grupo.id) {
    let result = await estudiosApi.getEstudioLink(grupo.id)
    if (!result.token) result = await estudiosApi.createEstudioLink(grupo.id)
    linkEstudiosUrl.value = result.token ? estudiosApi.getPublicUrl(result.token) : ''
    updateMessageDraft()
  }
}

const copyEstudiosLink = () => {
  if (!linkEstudiosUrl.value) return
  navigator.clipboard.writeText(linkEstudiosUrl.value)
  copiedEstudiosLink.value = true
  setTimeout(() => { copiedEstudiosLink.value = false }, 2000)
}

const openEstudiosLink = () => {
  if (!linkEstudiosUrl.value) return
  window.open(linkEstudiosUrl.value, '_blank', 'noopener')
}

const sendMessageWhatsapp = () => {
  const url = waLink(messageGroup.value?.whatsapp) + '?text=' + encodeURIComponent(messageDraft.value)
  window.open(url, '_blank', 'noopener')
  showMessageDialog.value = false
}

const loadGuestGroups = async () => {
  loadingGroups.value = true
  try {
    const result = await gruposInvitadosApi.getGruposInvitados()
    guestGroups.value = result.guestGroups || []
  } finally {
    loadingGroups.value = false
  }
}

const downloadGuestGroupsTemplate = () => {
  const content = buildGuestGroupsTemplateCsv(gruposPredicacion.value, zonas.value)
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style = 'display: none'
  a.href = url
  a.download = 'plantilla-grupos-invitados.csv'
  a.click()
  URL.revokeObjectURL(url)
}

const handleImportGroupsFile = (e) => {
  const file = e.files[0]
  if (!file) return
  const reader = new FileReader()
  reader.readAsText(file, 'UTF-8')
  reader.onload = async (result) => {
    try {
      const { rows, unresolvedGroups, unresolvedZonas } = parseGuestGroupsCsv(result.target.result, gruposPredicacion.value, zonas.value)
      const apiResult = await gruposInvitadosApi.bulkImportGruposInvitados(rows)
      if (apiResult.error) {
        toast.error('Ocurrió un error', apiResult.error)
        return
      }
      importSummary.value = { ...apiResult, unresolvedGroups, unresolvedZonas }
      showImportSummary.value = true
      await loadGuestGroups()
    } catch (err) {
      console.error(err)
      toast.error('Ocurrió un error', 'No se pudo procesar el archivo CSV.')
    }
  }
  reader.onerror = (err) => {
    console.error(err.target.result)
    toast.error('Ocurrió un error', 'No se pudo leer el archivo.')
  }
}

const openCreateGroup = () => {
  editingGroup.value = null
  groupForm.value = emptyGroupForm()
  errorGroup.value = null
  showGroupDialog.value = true
}

const openEditGroup = (group) => {
  editingGroup.value = group
  groupForm.value = {
    codigo: group.codigo,
    cantidad: group.cantidad,
    autos: group.autos,
    fechas: group.fechas,
    zonaId: group.zonaId,
    encargado: group.encargado,
    whatsapp: group.whatsapp,
    grupos: [...(group.grupos || [])],
    enGrupoWhatsapp: !!group.enGrupoWhatsapp,
    updatedAt: group.updatedAt, // para concurrencia optimista al guardar
  }
  errorGroup.value = null
  showGroupDialog.value = true
}

const assignZona = async (group, zonaId) => {
  const idx = guestGroups.value.findIndex((g) => g.id === group.id)
  if (idx === -1) return
  const previous = guestGroups.value[idx].zonaId
  guestGroups.value[idx] = { ...guestGroups.value[idx], zonaId }
  const result = await gruposInvitadosApi.updateGrupoInvitado(group.id, { ...group, zonaId })
  if (result.error) {
    guestGroups.value[idx] = { ...guestGroups.value[idx], zonaId: previous }
    if (result.code === 'STALE') {
      toast.error('Conflicto', 'Otro usuario modificó este grupo. Se recargó la lista.')
      await loadGuestGroups()
    } else {
      toast.error('Ocurrió un error', result.error)
    }
  } else if (result.updatedAt) {
    // Mantener el updatedAt fresco para no disparar falsos conflictos en el próximo cambio
    guestGroups.value[idx] = { ...guestGroups.value[idx], updatedAt: result.updatedAt }
  }
}

const toggleEnGrupoWhatsapp = async (group, val) => {
  const idx = guestGroups.value.findIndex((g) => g.id === group.id)
  if (idx === -1) return
  const previous = guestGroups.value[idx].enGrupoWhatsapp
  guestGroups.value[idx] = { ...guestGroups.value[idx], enGrupoWhatsapp: val }
  const result = await gruposInvitadosApi.updateGrupoInvitado(group.id, { ...group, enGrupoWhatsapp: val })
  if (result.error) {
    guestGroups.value[idx] = { ...guestGroups.value[idx], enGrupoWhatsapp: previous }
    if (result.code === 'STALE') {
      toast.error('Conflicto', 'Otro usuario modificó este grupo. Se recargó la lista.')
      await loadGuestGroups()
    } else {
      toast.error('Ocurrió un error', result.error)
    }
  } else if (result.updatedAt) {
    guestGroups.value[idx] = { ...guestGroups.value[idx], updatedAt: result.updatedAt }
  }
}

const saveGuestGroup = async () => {
  if (!groupForm.value.codigo?.trim()) {
    errorGroup.value = 'El código es requerido'
    return
  }
  savingGroup.value = true
  errorGroup.value = null
  try {
    const result = editingGroup.value
      ? await gruposInvitadosApi.updateGrupoInvitado(editingGroup.value.id, groupForm.value)
      : await gruposInvitadosApi.createGrupoInvitado(groupForm.value)
    if (result.error) {
      if (result.code === 'STALE') {
        errorGroup.value = 'Otro usuario modificó este grupo. Se recargó la lista, revisá y volvé a guardar.'
        await loadGuestGroups()
        return
      }
      errorGroup.value = result.error
      return
    }
    showGroupDialog.value = false
    await loadGuestGroups()
  } finally {
    savingGroup.value = false
  }
}

const confirmDeleteGroup = (group) => {
  confirm.require({
    message: `¿Eliminar el grupo de invitados "${group.codigo}"?`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await gruposInvitadosApi.deleteGrupoInvitado(group.id)
      await loadGuestGroups()
    },
  })
}

onMounted(async () => {
  await Promise.all([loadGuestGroups(), loadGruposPredicacion(), loadZonas(), loadPlantillas()])
})
</script>

<style scoped>
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
  text-align: left;
}
</style>
